/**
 * PipelineCustomizationRenderer - Generic renderer for customize_pipeline actions.
 *
 * Shows pipeline stages in an editable list with drag-and-drop reordering.
 * Validates stage names (no duplicates, no empty names) and requires minimum 2 stages.
 * Uses StageListEditor for the shared stage list layout.
 *
 * Domain-specific columns (e.g. probability for deals) are injected via the
 * `createColumns` factory — this component has no knowledge of any particular
 * pipeline type.
 */

import { AlertCircle, CheckCircle } from '@untitledui/icons';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Button } from '@/lib/base/buttons/button';
import type {
  StageColumn,
  StageItem,
  StageValidationErrors,
} from '@/lib/crm/components/stages/StageListEditor';
import { StageListEditor } from '@/lib/crm/components/stages/StageListEditor';

import { createProposalClient } from '../../../lib/proposals/core/ProposalClient';
import type {
  ActionPayload,
  ProposedAction,
} from '../../../lib/proposals/core/types';
import { usePipelineStagePersistence } from '../../../lib/proposals/hooks/usePipelineStagePersistence';
import type { ActionRendererProps } from '../../../lib/proposals/registry';
import { ActionError, ProposalContainer, ProposalInfoCallout } from '../shared';

// =============================================================================
// Types
// =============================================================================

/**
 * Payload structure for customize_pipeline action.
 * Stages are generic StageItem — domain-specific fields (probability, etc.)
 * live in the index signature and are read by injected columns.
 */
interface CustomizePipelinePayload {
  action_params: {
    action_key: 'customize_pipeline';
    title: string;
    description: string;
    action_label?: string;
    stages: StageItem[];
    explanation?: string;
  };
}

/** Mutation helpers passed to createColumns so column callbacks can update state. */
export interface StageMutationHelpers {
  /** Update a single field on a stage and persist */
  updateField: (id: string, field: string, value: unknown) => void;
  /** Mark a specific field on a stage as touched so its validation error becomes visible */
  markTouched: (id: string, field: string) => void;
}

export interface PipelineCustomizationRendererProps extends ActionRendererProps {
  /**
   * Factory that creates domain-specific columns. Receives mutation helpers
   * so column callbacks (e.g. probability input onChange) can update stage state.
   */
  createColumns?: (helpers: StageMutationHelpers) => StageColumn<StageItem>[];
  /** Extra fields to include when creating a new stage via "+ Add stage" */
  newStageDefaults?: Record<string, unknown>;
  /** Custom validation beyond name uniqueness/emptiness */
  validateStage?: (stage: StageItem) => StageValidationErrors[string] | null;
  /** Render extra info per stage in readonly mode (e.g. probability %) */
  renderReadonlyExtra?: (stage: StageItem) => ReactNode;
}

// =============================================================================
// Helpers
// =============================================================================

function isCustomizePipelinePayload(
  payload: unknown
): payload is CustomizePipelinePayload {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  const p = payload as Record<string, unknown>;
  const params = p.action_params;
  if (!params || typeof params !== 'object') {
    return false;
  }
  const ap = params as Record<string, unknown>;
  return ap.action_key === 'customize_pipeline' && Array.isArray(ap.stages);
}

/**
 * Validate stage names — no empty names, no duplicates (case-insensitive).
 */
function validateStageNames(
  stages: StageItem[]
): Map<string, { name: string }> {
  const errors = new Map<string, { name: string }>();
  const seenNames = new Map<string, string>();

  for (const stage of stages) {
    if (!stage.name.trim()) {
      errors.set(stage.id, { name: 'Stage name cannot be empty' });
    } else {
      const lowerName = stage.name.trim().toLowerCase();
      const existing = seenNames.get(lowerName);
      if (existing !== undefined) {
        errors.set(stage.id, {
          name: `Duplicate stage name "${existing}"`,
        });
      } else {
        seenNames.set(lowerName, stage.name.trim());
      }
    }
  }

  return errors;
}

// =============================================================================
// Main Renderer
// =============================================================================

/**
 * How long to wait after the last edit before writing the payload back.
 * Without this we issue one PUT per keystroke, and each response echoes a
 * stale payload back into the controlled inputs.
 */
const PERSIST_DEBOUNCE_MS = 500;

export function PipelineCustomizationRenderer({
  action,
  mode,
  className = '',
  onUpdate,
  proposalId,
  orgId,
  createColumns,
  newStageDefaults,
  validateStage,
  renderReadonlyExtra,
}: PipelineCustomizationRendererProps) {
  // Extract payload BEFORE hooks
  const validPayload = isCustomizePipelinePayload(action.payload);
  const payload = validPayload
    ? (action.payload as CustomizePipelinePayload)
    : null;
  const initialStages = useMemo(
    () => payload?.action_params?.stages ?? [],
    [payload?.action_params?.stages]
  );
  const explanation = payload?.action_params?.explanation ?? '';

  // Proposal client for persisting updates
  const proposalClient = useMemo(
    () => (orgId ? createProposalClient(orgId) : null),
    [orgId]
  );

  // Local state
  const [stages, setStages] = useState<StageItem[]>(initialStages);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Each successful update_payload mints a NEW action version (new id); the
  // old id 404s on the next save. Track the current id and adopt the server's
  // returned action after every save. Same pattern as CreatePipelineRenderer.
  const actionIdRef = useRef(action.id);
  useEffect(() => {
    actionIdRef.current = action.id;
  }, [action.id]);

  // Persist to backend
  const persistFn = useCallback(
    async (updatedStages: StageItem[]) => {
      if (!proposalClient || !proposalId || !payload) {
        return;
      }
      const updatedPayload: CustomizePipelinePayload = {
        ...payload,
        action_params: {
          ...payload.action_params,
          stages: updatedStages,
        },
      };
      const updatedAction = await proposalClient.updateActionPayload(
        proposalId,
        actionIdRef.current,
        {
          payload: updatedPayload as unknown as ActionPayload,
        }
      );
      actionIdRef.current = updatedAction.id;
      onUpdate?.(updatedAction);
    },
    [proposalClient, proposalId, payload, onUpdate]
  );

  // Captured once so the coordination registration survives any action-id
  // churn from saves round-tripping through `onUpdate`.
  const coordinationIdRef = useRef(`customize-pipeline-${action.id}`);
  // Captured once at mount so change detection can suppress writes when the
  // user edits and then reverts to the original server value.
  const initialSavedValueRef = useRef(initialStages);

  const {
    notifyEdit,
    flushPersist,
    apiError,
    isGuardActive,
    retryNow,
    syncSavedValue,
  } = usePipelineStagePersistence<StageItem[]>(persistFn, {
    debounceMs: PERSIST_DEBOUNCE_MS,
    errorMessage: "We couldn't save the stage changes. Try again.",
    logContext: { proposalId, actionId: actionIdRef.current },
    coordinationId: coordinationIdRef.current,
    initialSavedValue: initialSavedValueRef.current,
  });
  // Mirrored into a ref so the sync effect below can read the LATEST guard
  // value without depending on it reactively — the effect must only re-run
  // when `initialStages` itself changes (matching the pre-extraction
  // dependency array), not whenever the guard happens to flip.
  const isGuardActiveRef = useRef(isGuardActive);
  isGuardActiveRef.current = isGuardActive;

  // Sync when action prop changes.
  //
  // Every save rewrites the payload server-side, and the response — plus any
  // proposal WebSocket update or refetch — comes back as a NEW stages array.
  // Adopting it while the user is mid-word replaces what they have typed with
  // the server's copy from a keystroke or two ago, which reads as characters
  // being deleted, swapped, or dropped. Hold local state until the server has
  // caught up with every edit. Same guard as the board's inline rename
  // (`lib/crm/pipeline/components/PipelineColumn.tsx`).
  useEffect(() => {
    if (isGuardActiveRef.current) {
      return;
    }
    // Advance the change-detection baseline to whatever we're adopting. After a
    // local save this is a no-op (the baseline already equals the saved value),
    // but when the value arrived from an external update (WebSocket/refetch)
    // this keeps the baseline in step so a later revert to it is not written.
    syncSavedValue(initialStages);
    setStages(initialStages);
  }, [initialStages, syncSavedValue]);

  // Mark a specific field on a stage as touched
  const markTouched = useCallback((id: string, field: string) => {
    setTouchedFields(prev => {
      const key = `${id}:${field}`;
      if (prev.has(key)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  // Notify parent + persist
  const notifyUpdate = useCallback(
    (updatedStages: StageItem[]) => {
      if (!payload) {
        return;
      }

      if (onUpdate) {
        const updatedPayload: CustomizePipelinePayload = {
          ...payload,
          action_params: {
            ...payload.action_params,
            stages: updatedStages,
          },
        };
        // Use the latest action id (tracked in actionIdRef), not the prop's
        // possibly-stale value: after a save the prop's `action.id` may not yet
        // reflect the server's new versioned id, and a subsequent edit before
        // the prop catches up would otherwise target the old (now-404) action.
        onUpdate({
          ...action,
          id: actionIdRef.current,
          payload: updatedPayload,
        } as ProposedAction);
      }

      notifyEdit(updatedStages);
    },
    [action, payload, onUpdate, notifyEdit]
  );

  // Generic field update — used by domain-specific columns via StageMutationHelpers.
  // Uses refs to always read current state without re-creating the columns.
  const stagesRef = useRef(stages);
  stagesRef.current = stages;
  const notifyUpdateRef = useRef(notifyUpdate);
  notifyUpdateRef.current = notifyUpdate;

  const updateField = useCallback(
    (id: string, field: string, value: unknown) => {
      const updated = stagesRef.current.map(s =>
        s.id === id ? { ...s, [field]: value } : s
      );
      setStages(updated);
      notifyUpdateRef.current(updated);
    },
    []
  );

  const mutationHelpers = useMemo<StageMutationHelpers>(
    () => ({ updateField, markTouched }),
    [updateField, markTouched]
  );

  // Build columns via factory (stable — only recreated if factory changes)
  const columns = useMemo(
    () => createColumns?.(mutationHelpers) ?? [],
    [createColumns, mutationHelpers]
  );

  // Validation — name checks + optional domain-specific checks
  const allValidationErrors = useMemo(() => {
    const nameErrors = validateStageNames(stages);
    const merged: StageValidationErrors = {};

    for (const stage of stages) {
      const nameErr = nameErrors.get(stage.id);
      const customErr = validateStage?.(stage);

      if (nameErr || customErr) {
        merged[stage.id] = { ...nameErr, ...customErr };
      }
    }

    return merged;
  }, [stages, validateStage]);

  const visibleErrors = useMemo(() => {
    const filtered: StageValidationErrors = {};
    for (const [id, errs] of Object.entries(allValidationErrors)) {
      const visibleFieldErrors: Record<string, string | undefined> = {};
      let hasVisible = false;
      for (const [field, msg] of Object.entries(errs)) {
        if (touchedFields.has(`${id}:${field}`)) {
          visibleFieldErrors[field] = msg;
          hasVisible = true;
        }
      }
      if (hasVisible) {
        filtered[id] = visibleFieldErrors;
      }
    }
    return filtered;
  }, [allValidationErrors, touchedFields]);

  const hasVisibleErrors = Object.keys(visibleErrors).length > 0;

  const handleNameChange = useCallback(
    (id: string, name: string) => {
      const updated = stages.map(s => (s.id === id ? { ...s, name } : s));
      setStages(updated);
      notifyUpdate(updated);
    },
    [stages, notifyUpdate]
  );

  const handleNameBlur = useCallback(
    (id: string) => {
      markTouched(id, 'name');
      // Don't make the user wait out the debounce once they've left the field.
      flushPersist();
    },
    [markTouched, flushPersist]
  );

  const handleColorChange = useCallback(
    (id: string, color: string | null) => {
      const updated = stages.map(s =>
        s.id === id ? { ...s, color: color ?? undefined } : s
      );
      setStages(updated);
      notifyUpdate(updated);
    },
    [stages, notifyUpdate]
  );

  const handleRemove = useCallback(
    (id: string) => {
      if (stages.length <= 2) {
        return;
      }
      const updated = stages.filter(s => s.id !== id);
      setStages(updated);
      notifyUpdate(updated);
    },
    [stages, notifyUpdate]
  );

  const handleAdd = useCallback(() => {
    const newStage: StageItem = {
      id: `stage-${Date.now()}`,
      name: '',
      ...newStageDefaults,
    };
    const updated = [...stages, newStage];
    setStages(updated);
    notifyUpdate(updated);
  }, [stages, notifyUpdate, newStageDefaults]);

  const handleReorder = useCallback(
    (reordered: StageItem[]) => {
      setStages(reordered);
      notifyUpdate(reordered);
    },
    [notifyUpdate]
  );

  const canRemove = stages.length > 2;
  const resolveCanDelete = useCallback(() => canRemove, [canRemove]);

  // Early return AFTER all hooks
  if (!validPayload) {
    return (
      <ActionError
        message='Invalid pipeline customization payload'
        mode={mode}
      />
    );
  }

  // Readonly mode
  if (mode === 'readonly') {
    return (
      <div className={`space-y-3 ${className}`}>
        {explanation && (
          <p className='text-sm text-fg-secondary'>{explanation}</p>
        )}
        <div className='space-y-1.5'>
          {stages.map((stage, i) => (
            <div
              key={stage.id}
              className='flex items-center gap-3 px-3 py-2 bg-bg-secondary rounded-lg'
            >
              <span className='flex items-center justify-center w-5 h-5 rounded-full bg-success-100 text-[10px] font-semibold text-success-700 shrink-0'>
                {i + 1}
              </span>
              <CheckCircle className='w-4 h-4 text-success-600 shrink-0' />
              <span className='flex-1 text-sm font-medium text-fg-primary'>
                {stage.name}
              </span>
              {renderReadonlyExtra?.(stage)}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Full mode — editable
  if (mode === 'full') {
    return (
      <ProposalContainer className={className}>
        {explanation && (
          <ProposalInfoCallout>{explanation}</ProposalInfoCallout>
        )}

        {hasVisibleErrors && (
          <div className='flex items-start gap-2.5 px-3.5 py-2.5 bg-error-50 border border-error-200 rounded-lg'>
            <AlertCircle className='w-4 h-4 text-error-600 shrink-0 mt-0.5' />
            <p className='text-sm text-error-700'>
              Please fix validation errors before proceeding.
            </p>
          </div>
        )}

        {stages.length < 2 && (
          <div className='flex items-start gap-2.5 px-3.5 py-2.5 bg-warning-50 border border-warning-200 rounded-lg'>
            <AlertCircle className='w-4 h-4 text-warning-600 shrink-0 mt-0.5' />
            <p className='text-sm text-warning-700'>
              Pipeline must have at least 2 stages.
            </p>
          </div>
        )}

        {apiError && (
          <div className='flex items-center justify-between gap-3 rounded-lg border border-utility-error-200 bg-utility-error-50 p-4'>
            <p className='text-sm text-utility-error-700'>{apiError}</p>
            <Button color='tertiary' size='sm' onClick={retryNow}>
              Retry
            </Button>
          </div>
        )}

        <StageListEditor
          stages={stages}
          columns={columns}
          bordered
          showColumnHeaders
          onNameChange={handleNameChange}
          onNameBlur={handleNameBlur}
          onColorChange={handleColorChange}
          onRemove={handleRemove}
          onReorder={handleReorder}
          onAdd={handleAdd}
          canDelete={resolveCanDelete}
          validationErrors={visibleErrors}
        />
      </ProposalContainer>
    );
  }

  // Unsupported mode fallback
  return (
    <div className={className}>
      <span className='text-sm text-fg-secondary'>
        Customize pipeline (mode: {mode})
      </span>
    </div>
  );
}
